<template>
	<div id="modal" v-if="showModal" @keydown.esc="closeModal" tabindex="-1" ref="modal">
		<div :class="systemStore.modal.bigMode ? 'big':''" class="modalWrapper">
			<div class="title">{{ modalTitle }}</div>
			<div class="popupContent" >
				<!--  Variable content -->
				<div>
					<div class="w-full popupInputs" id="popupWrapper">

						<div v-if="loading" class="text-center">
							<LoadingSpinner/>
						</div>
						<div v-else>
              <div v-if="modalType === PopupType.selectSimilarProvider">
                <SimilarProviderSelectionPopup :data="dataCopy"/>
              </div>
							<div v-else-if="modalType === PopupType.providerManagementEditPopup">
								<ProviderEditPopup :data="dataCopy"/>
							</div>
              <div v-else-if="modalType === PopupType.providerNewProviderWorkerPopup">
                <NewProviderWorkerPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.providerProviderWorkerEditPopup">
                <ProviderWorkerEditPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.plantDetailsPopup">
                <PlantDetailsPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.addOrUpdatePlantPopup">
                <AddOrUpdatePlantPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.formReportChangeOwnerPopup">
                <FormReportChangeOwnerPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.formReportFilterPopup">
                <FormReportFilterPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.editGlobalSettingsPopup">
                <EditGlobalSettingsPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.addOrUpdateUserPopup">
                <AddOrUpdateUserPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.importPlantsPopup">
                <ImportPlantsPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.importUsersPopup">
                <ImportUsersPopup :data="dataCopy"/>
              </div>
							<div v-else>
								{{ PopupType[PopupType[modalType]] }} Popup
							</div>
						</div>
					</div>
				</div>



			</div>
			<div class="footer flex">
				<div class="flex-1">
					<div class="floatingError mr-4">
						<Error :errorLoc="ErrorLocationType.popup"/>
					</div>
					<button class="btn mr-2" v-if="resetButtonState" @click="resetPreferences()">{{t('general.reset')}}<span class="icon-reset ml-1"></span></button>
				</div>

				<div class="text-right">
          <button class="btn mr-2" @click="closeModal()" v-if="props.showCancelButton">{{t('general.cancel')}}</button>
          <button class="primary" @click="confirmModal()" v-if="isFilterPopup()" :disabled="loadingConfirm">
            <TextOrSpinner :text="`${lengthAfterFilter} ${t('general.results')}`" :spin="loadingConfirm"/>
          </button>
          <button class="primary" id="okButton" @click="confirmModal()" v-else-if="popupNeedsCallbackButton()" :disabled="loadingConfirm">
            <TextOrSpinner :text="t('general.confirm')" :spin="loadingConfirm"/>
          </button>
				</div>

			</div>
		</div>

	</div>
</template>

<script setup>
import {computed, defineProps, ref, watchEffect} from 'vue';
import { useSystemStore } from "@/stores/system/SystemStore";
import { PopupType } from "@/utilities/system/enums/Enums";
import LoadingSpinner from "@/components/system/LoadingSpinner.vue";
import Error from "./system/Error.vue";
import { ErrorLocationType } from "@/utilities/system/enums/Enums";
import {useI18n} from "vue-i18n";
import SimilarProviderSelectionPopup from "@/components/popups/provider/SimilarProviderSelectionPopup";
import ProviderEditPopup from "@/components/popups/provider/ProviderEditPopup";
import NewProviderWorkerPopup from "@/components/popups/providerWorker/NewProviderWorkerPopup";
import ProviderWorkerEditPopup from "@/components/popups/providerWorker/ProviderWorkerEditPopup";
import PlantDetailsPopup from "@/components/popups/plant/PlantDetailsPopup";
import AddOrUpdatePlantPopup from "@/components/popups/plant/AddOrUpdatePlantPopup";
import ImportPlantsPopup from "@/components/popups/plant/ImportPlantsPopup";
import FormReportChangeOwnerPopup from "@/components/popups/formReport/FormReportChangeOwnerPopup";
import FormReportFilterPopup from "@/components/popups/formReport/FormReportFilterPopup";
import EditGlobalSettingsPopup from "@/components/popups/setting/EditGlobalSettingsPopup";
import AddOrUpdateUserPopup from "@/components/popups/user/AddOrUpdateUserPopup";
import ImportUsersPopup from "@/components/popups/user/ImportUsersPopup";
import TextOrSpinner from "@/components/TextOrSpinner";


const { t } = useI18n();

const props = defineProps({
	showCancelButton: {
		type: Boolean,
		default: true
	},
	showResetButton: {
		type: Boolean,
		default: true
	},
	data: Object
});

const loading = ref(false);
const loadingConfirm = ref(false);
const systemStore = useSystemStore();

// Make a REF Copy of the Data
const dataCopy = ref(null);

// make flat copy of props.data by hand, otherwise v-model pushes changes from the popup through to the record data in the grid, which is buggy and weird (grid data changes but db-data doesnt)
watchEffect(()=>{
  const copy = {};
  for (const keyValueArray of Object.entries(props.data)){
    copy[keyValueArray[0]] = keyValueArray[1];
  }
  dataCopy.value = copy;
})

const isFilterPopup = ()=>{
  return modalType.value === PopupType.formReportFilterPopup
};

const popupNeedsCallbackButton = ()=>{
  return modalType.value !== PopupType.plantDetailsPopup;
}

const showModal = computed(() => {
	return systemStore.modal.show;
});

const modalTitle = computed(() => {
	return systemStore.modal.title;
});

const modalType = computed(() => {
	return systemStore.modal.popupType;
});

const resetButtonState = computed(() => {
	return systemStore.modal.resetButton;
});

const lengthAfterFilter = computed(()=>{
	return systemStore.countAfterFilters;
});


const closeModal = async () => {
  systemStore.error.location = ErrorLocationType.none;
	systemStore.closeModal();
};

const confirmModal = async () => {
  loadingConfirm.value = true;

  // update contentObject before proceeding with save action (not done automatically since it is flatCopy)
  systemStore.modal.contentObject = dataCopy.value;

	const callback = systemStore.modal.action;
	const isValid = await systemStore.modal.validator.$validate();

  try{
    // check if callback action is a function and run validation
    if(typeof(callback) === "function" && isValid) await callback(); // closing should be done via the callback
    else {
      systemStore.error.type = "error";
      systemStore.error.text = t("global.validationFailed");
      systemStore.error.code = 0;
      systemStore.error.location = ErrorLocationType.popup;

      const targetElement  = document.querySelector(".error");
      targetElement.scrollIntoView({behavior: "smooth", block: 'center' });
    }
  }
  catch (ex) {
    console.log("error when callback action", ex)
  }
  finally {
    loadingConfirm.value = false;
  }
};

const resetPreferences = () => {
	const filteredDefaultHiddenCols = systemStore.settings.defaultHiddenColumns.filter((dhc)=>{
		switch(modalType.value){
			case PopupType.formReportFilterPopup:
        return dhc.tableName === "form";
			default:
				console.log("PopupType not know when resetting filters");
				return false;
		}
	});
	systemStore.setTmpFilters([]);
	systemStore.setTmpHiddenColumns(filteredDefaultHiddenCols);
	systemStore.setCountAfterFilters(systemStore.gridStore.storage.allValues.length);
};

</script>

<style lang="scss">
	#modal {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100vh;
		position: fixed;
		top: 0;
		z-index: 4999;
		background: rgba(0,0,0,0.5);
		flex-flow: column;
		.modalWrapper {
			@apply rounded-xl;
			background: $white;
			width: 800px;
		}
		.title {
			@apply w-full rounded-t-xl  pb-2 mb-2 text-center py-3;
			color: $nightGrey;
			background: $white;
			border-bottom: 1px solid $foggyGrey;
		}
		.inputTitle {
			@apply mb-1;
			font-size: 12px;
			color: $nightGrey;
			font-family: 'Open Sans Regular', sans-serif;
		}
		.inputRow {
			@apply my-3 p-1;
			display: flex;
			flex-direction: column;
			flex-basis: 100%;
			flex: 1;
		}
		.groupTitle {
			@apply mb-4;
		}
		.groupInputs {
			flex-direction: row;
			flex-wrap: wrap;
			column-gap: 20px;
			width: 100%;
		}
		.inputGroup {
			width: 100%;
			@apply mb-10 pb-10;
			border-bottom: 1px solid $foggyGrey;
		}
		.popupContent {
			padding: 20px;
			.popupInputs {
				@apply pr-4;
				max-height: 60vh;
				overflow-y: auto;
			}
		}
		.footer {
			@apply pt-2 mt-2 rounded-b-xl;
			padding: 20px;
			background: $white;
			border-top: 1px solid $foggyGrey;
		}
		.floatingError {
			//position: absolute;
			//width: calc(100% - 65px);
			//bottom: 0px;
		}
	}
  .big {
    width: 1000px !important;
  }
</style>
